import * as React from 'react'
import { useDatepicker, UseDatepickerProps } from '@datepicker-react/hooks'
import Month from './month'
import DateRangeContext from '../configurations/date.range.context'
import * as styles from '../date.pickers.styl'

interface DateRangeProps extends React.PropsWithChildren<UseDatepickerProps> {
  onClickOutsideHandler?: (event: Event) => void
  onClose?: () => void
}

export const DateRange = (
  {
    onClickOutsideHandler,
    children,
    onClose,
    onDatesChange,
    startDate,
    endDate,
    focusedInput
  }: DateRangeProps,
  ref?: React.Ref<unknown>
) => {
  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateBlocked,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToPreviousMonths,
    goToNextMonths
  } = useDatepicker({
    startDate,
    endDate,
    focusedInput,
    onDatesChange,
    numberOfMonths: 2
  })

  React.useImperativeHandle(ref, () => ({ onDateSelect }))

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('mousedown', onClickOutsideHandler)
      window.addEventListener('touchstart', onClickOutsideHandler)
    }
    return () => {
      if (onClose) {
        onClose()
      }
      window.removeEventListener('mousedown', onClickOutsideHandler)
      window.removeEventListener('touchstart', onClickOutsideHandler)
    }
  }, [])

  return (
    <DateRangeContext.Provider
      value={{
        startDate,
        endDate,
        focusedInput,
        activeMonths,
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked,
        isFirstOrLastSelectedDate,
        onDateSelect,
        onDateFocus,
        onDateHover
      }}
    >
      <div className={styles.datePickerWrapper} role="date-picker-wrapper">
        <div
          className={styles.datePickerContext}
          style={{
            gridTemplateColumns: `repeat(${activeMonths.length}, 300px)`
          }}
        >
          <svg focusable="false" className={styles.fang}>
            <path className={styles.fangShape} d="M0,10 20,10 10,0z" />
            <path className={styles.fangStroke} d="M0,10 10,0 20,10" />
          </svg>
          <div className={styles.monthButtons}>
            <button type="button" onClick={goToPreviousMonths}>
              &#8592;
            </button>
            <button type="button" onClick={goToNextMonths}>
              &#8594;
            </button>
          </div>
          {activeMonths.map(month => (
            <Month
              key={`${month.year}-${month.month}`}
              year={month.year}
              month={month.month}
              firstDayOfWeek={firstDayOfWeek}
            />
          ))}
          {children}
        </div>
      </div>
    </DateRangeContext.Provider>
  )
}

export default React.memo(React.forwardRef(DateRange))
